/*  ============================================================================
    TimeReconcileTab.jsx — "Missing Time": calendar meetings that look like
    unbilled work. Autotask → Missing Time · tab id "timereconcile".

    Phase 1 is CALENDAR ONLY. Every row carries `source: "calendar"`; sent-mail
    threads (Phase 2) slot in beside them without a rewrite.

    ⚠ NOTHING IS WRITTEN UNTIL CRAIG PRESSES SUBMIT. Scan is read-only; a
      company change calls /check (read-only); the two row actions open the
      existing To-Do ticket modal in MspOsApp (New ticket + time) or the
      AddTimeModal here (Add time to ticket). Dismissals are the only write
      this tab makes itself, and they store subject + date only.

    ⚠ COVERAGE IS SERVER-SIDE, ALWAYS. The tab never decides "covered" itself:
      changing a company sends the row's key/times/subject to /check and
      replaces that ONE row's company + coverage from the answer. Each row has
      its own spinner; the rest of the list stays put.

    ⚠ NO SIDE PANELS. Not in FOCUS_PANEL_TABS or INBOX_PANEL_TABS; the root is
      width:100% + flex:1 + minWidth:0 with its own scroll container.

    Company field is the shared ClientTypeahead over ACTIVE companies only
    (`isActive !== false`). Never match a company by NAME here — the server
    resolves by contact email / ticket number / domain, and a typeahead pick is
    an explicit manual choice, badged Manual.
    ============================================================================ */

import { useState, useMemo, useCallback, useRef } from "react";
import ClientTypeahead from "../common/ClientTypeahead";
import AddTimeModal from "./AddTimeModal";
import { scanRange, checkItem, dismissItem, restoreItem } from "../../api/timeReconcileApi";
import {
  defaultRange, rangeError, filterByChip, chipCounts, groupByDate, fmtDateHeading, fmtDuration,
  buildTicketPrefill, applyCheck, externalAttendees, STATUS_META, SOURCE_LABEL, DISMISS_REASONS, CHIPS,
} from "./reconcileRules";

const errMsg = (e) =>
  e?.response?.data?.error || e?.response?.data?.message || (e?.response?.status ? `HTTP ${e.response.status}` : (e?.message || "request failed"));

const card = { background: "white", border: "1px solid var(--border)", borderRadius: 8, padding: "12px 14px", marginBottom: 10 };
const sub = { fontSize: 10.5, color: "var(--text3)", lineHeight: 1.5 };
const pill = (color, bg) => ({ display: "inline-block", fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 10, color, background: bg, border: `1px solid ${color}`, whiteSpace: "nowrap" });

function StatusChip({ status }) {
  const m = STATUS_META[status] || { label: status, color: "var(--text3)", bg: "rgba(0,0,0,0.06)" };
  return <span data-testid="status-chip" style={pill(m.color, m.bg)}>{m.label}</span>;
}

function SourceBadge({ source }) {
  if (!source) return null;
  return <span data-testid="source-badge" style={{ ...pill("var(--strat)", "var(--strat-d)"), fontWeight: 500 }}>{SOURCE_LABEL[source] || source}</span>;
}

function Row({ item, companies, busy, onPickCompany, onNewTicket, onAddTime, onDismiss, onRestore }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const ext = externalAttendees(item);
  const cov = item.coverage || { status: "unresolved", entries: [], candidateTickets: [] };
  const hasCompany = !!item.company?.id;
  const dim = item.dismissed ? { opacity: 0.6 } : null;

  return (
    <div data-testid="reconcile-row" data-key={item.key} style={{ ...card, ...dim }}>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, color: "var(--text3)", fontVariantNumeric: "tabular-nums" }}>{item.startLocal}–{item.endLocal} · {fmtDuration(item.durationMins)}</span>
            <span style={{ fontSize: 12.5, fontWeight: 600 }}>{item.subject}</span>
            <StatusChip status={cov.status} />
            {item.dismissed && <span style={pill("var(--text3)", "rgba(0,0,0,0.05)")}>Dismissed · {DISMISS_REASONS.find((r) => r.id === item.dismissed.reason)?.label || item.dismissed.reason}</span>}
          </div>
          <div style={{ ...sub, marginTop: 3 }}>
            {ext.length ? ext.map((a) => (a.name && a.name.toLowerCase() !== a.email ? `${a.name} <${a.email}>` : a.email)).join(" · ") : "No external attendees"}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <div style={{ width: 280, maxWidth: "100%" }}>
              <ClientTypeahead
                value={item.company?.name || ""}
                companies={companies}
                placeholder={item.ambiguous ? "Shared domain — pick one" : "Pick the company…"}
                inputStyle={{ marginBottom: 0, fontSize: 11 }}
                onChange={(_name, matched) => { if (matched) onPickCompany(item, matched); }}
              />
            </div>
            <SourceBadge source={item.company?.source} />
            {busy && <span data-testid="row-spinner" style={{ ...sub, color: "var(--strat)" }}>Checking…</span>}
          </div>

          {item.ambiguous && (
            <div data-testid="ambiguous" style={{ marginTop: 6, padding: "6px 10px", borderRadius: 6, background: "rgba(230,126,34,0.12)", border: "1px solid rgba(230,126,34,0.4)", fontSize: 11 }}>
              <span style={{ fontWeight: 600, color: "var(--warn)" }}>Shared domain — pick one:</span>{" "}
              {(item.companyCandidates || []).map((c) => (
                <button key={c.id} type="button" className="btn" style={{ fontSize: 10.5, padding: "2px 8px", marginLeft: 4 }}
                  onClick={() => onPickCompany(item, { id: c.id, companyName: c.name })}>{c.name}</button>
              ))}
            </div>
          )}

          {(cov.status === "covered" || cov.status === "possibly-covered") && cov.entries?.length > 0 && (
            <div data-testid="entries" style={{ marginTop: 6, fontSize: 11 }}>
              {cov.entries.map((e) => (
                <div key={e.id} style={{ color: "var(--text2)" }}>
                  ⏱ {e.ticketNumber || `#${e.ticketID}`} {e.ticketTitle ? `· ${e.ticketTitle}` : ""} · {e.startLocal && e.endLocal ? `${e.startLocal}–${e.endLocal}` : "no start/end"} · {e.hoursWorked}h
                  {cov.status === "possibly-covered" && <span style={{ color: "var(--warn)" }}> (same day, no overlap)</span>}
                </div>
              ))}
            </div>
          )}

          {cov.status === "ticket-no-time" && cov.candidateTickets?.length > 0 && (
            <div style={{ marginTop: 6, fontSize: 11, color: "var(--text2)" }}>
              Likely ticket: {cov.candidateTickets.filter((t) => t.score > 0).slice(0, 3).map((t) => `${t.ticketNumber} · ${t.title}`).join("  |  ")}
            </div>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }}>
          {item.dismissed ? (
            <button type="button" className="btn" style={{ fontSize: 11 }} onClick={() => onRestore(item)}>↩ Restore</button>
          ) : (
            <>
              <button type="button" className="btn btn-O" style={{ fontSize: 11 }} disabled={!hasCompany || busy} title={hasCompany ? "" : "Pick a company first"}
                onClick={() => onNewTicket(item)}>🎫 New ticket + time</button>
              <button type="button" className="btn btn-S" style={{ fontSize: 11 }} disabled={!hasCompany || busy} title={hasCompany ? "" : "Pick a company first"}
                onClick={() => onAddTime(item)}>⏱ Add time to ticket</button>
              <div style={{ position: "relative" }}>
                <button type="button" className="btn" style={{ fontSize: 11, width: "100%" }} onClick={() => setMenuOpen((o) => !o)}>Dismiss ▾</button>
                {menuOpen && (
                  <div role="menu" style={{ position: "absolute", right: 0, top: "100%", zIndex: 20, background: "white", border: "1px solid var(--border)", borderRadius: 6, boxShadow: "var(--shadow-md)", minWidth: 160 }}>
                    {DISMISS_REASONS.map((r) => (
                      <div key={r.id} role="menuitem" style={{ padding: "6px 10px", fontSize: 11, cursor: "pointer" }}
                        onMouseDown={(e) => { e.preventDefault(); setMenuOpen(false); onDismiss(item, r.id); }}>{r.label}</div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TimeReconcileTab({ atCompanies = [], atReference = null, onNewTicket = null }) {
  const initial = useMemo(() => defaultRange(), []);
  const [start, setStart] = useState(initial.start);
  const [end, setEnd] = useState(initial.end);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [scan, setScan] = useState(null);          // { range, resourceId, counts }
  const [items, setItems] = useState([]);
  const [chip, setChip] = useState("attention");
  const [busy, setBusy] = useState({});            // key → true while /check runs
  const [addTimeFor, setAddTimeFor] = useState(null);
  const [toast, setToast] = useState("");
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const activeCompanies = useMemo(() => (atCompanies || []).filter((c) => c.isActive !== false), [atCompanies]);
  const spanErr = rangeError(start, end);
  const counts = useMemo(() => chipCounts(items), [items]);
  const visible = useMemo(() => filterByChip(items, chip), [items, chip]);
  const groups = useMemo(() => groupByDate(visible), [visible]);

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3500); };

  const runScan = useCallback(async () => {
    if (spanErr) return;
    setLoading(true); setError("");
    try {
      const data = await scanRange(start, end);
      setScan({ range: data.range, resourceId: data.resourceId, counts: data.counts });
      setItems(data.items || []);
    } catch (e) {
      setError(`Scan failed: ${errMsg(e)}`);
    } finally { setLoading(false); }
  }, [start, end, spanErr]);

  /** /check for ONE row; only that row's company + coverage change. */
  const recheck = useCallback(async (item, companyId) => {
    setBusy((b) => ({ ...b, [item.key]: true }));
    try {
      const res = await checkItem(item, companyId);
      setItems((cur) => applyCheck(cur, item.key, res));
    } catch (e) {
      flash(`Check failed for "${item.subject}": ${errMsg(e)}`);
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[item.key]; return n; });
    }
  }, []);

  const onPickCompany = (item, matched) => { if (matched?.id != null) recheck(item, matched.id); };

  const onNewTicketClick = (item) => {
    if (!onNewTicket || !item.company?.id) return;
    const prefill = buildTicketPrefill(item);
    onNewTicket(prefill, () => {
      const latest = itemsRef.current.find((it) => it.key === item.key) || item;
      recheck(latest, latest.company.id);
    });
  };

  const onDismiss = async (item, reason) => {
    try {
      const res = await dismissItem(item, reason);
      setItems((cur) => cur.map((it) => (it.key === item.key ? { ...it, dismissed: res.dismissed } : it)));
    } catch (e) { flash(`Dismiss failed: ${errMsg(e)}`); }
  };
  const onRestore = async (item) => {
    try {
      await restoreItem(item.key);
      setItems((cur) => cur.map((it) => (it.key === item.key ? { ...it, dismissed: null } : it)));
    } catch (e) { flash(`Restore failed: ${errMsg(e)}`); }
  };

  return (
    <div data-testid="time-reconcile-root" style={{ width: "100%", flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden", background: "var(--bg)" }}>
      <div style={{ padding: "12px 20px", borderBottom: "1px solid var(--border)", background: "white", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>⏱ Missing Time</div>
            <div style={sub}>Calendar meetings with external attendees, checked against your Autotask time entries. Nothing is written until you submit a ticket or time entry.</div>
          </div>
          <div style={{ flex: 1 }} />
          <label style={{ fontSize: 10.5, color: "var(--text3)" }}>Start<br />
            <input type="date" className="inp" aria-label="Start date" style={{ marginBottom: 0, width: 150 }} value={start} onChange={(e) => setStart(e.target.value)} />
          </label>
          <label style={{ fontSize: 10.5, color: "var(--text3)" }}>End<br />
            <input type="date" className="inp" aria-label="End date" style={{ marginBottom: 0, width: 150 }} value={end} onChange={(e) => setEnd(e.target.value)} />
          </label>
          <button type="button" className="btn btn-strat" disabled={!!spanErr || loading} onClick={runScan}>{loading ? "Scanning…" : "Scan"}</button>
        </div>
        {spanErr && <div data-testid="range-error" style={{ color: "var(--danger)", fontSize: 11, marginTop: 6 }}>{spanErr}</div>}
        {error && <div data-testid="scan-error" style={{ color: "var(--danger)", fontSize: 11, marginTop: 6 }}>{error}</div>}
        {scan && (
          <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
            {CHIPS.map((c) => (
              <button key={c.id} type="button" className={`btn ${chip === c.id ? "btn-strat" : ""}`} data-testid={`chip-${c.id}`} style={{ fontSize: 11, padding: "4px 10px" }} onClick={() => setChip(c.id)}>
                {c.label} <span style={{ opacity: 0.7 }}>({counts[c.id] ?? 0})</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="scroll" style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "14px 20px 40px" }}>
        {toast && <div style={{ ...card, color: "var(--danger)", fontSize: 11 }}>{toast}</div>}
        {!scan && !loading && <div style={{ ...sub, fontSize: 12, padding: 20, textAlign: "center" }}>Pick a range (up to 31 days) and press Scan.</div>}
        {scan && groups.length === 0 && <div style={{ ...sub, fontSize: 12, padding: 20, textAlign: "center" }}>Nothing to show for this filter.</div>}
        {groups.map((g) => (
          <div key={g.date} data-testid="date-group">
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text2)", margin: "6px 0 8px", textTransform: "uppercase", letterSpacing: ".06em" }}>{fmtDateHeading(g.date)}</div>
            {g.items.map((it) => (
              <Row key={it.key} item={it} companies={activeCompanies} busy={!!busy[it.key]}
                onPickCompany={onPickCompany} onNewTicket={onNewTicketClick} onAddTime={setAddTimeFor} onDismiss={onDismiss} onRestore={onRestore} />
            ))}
          </div>
        ))}
      </div>

      {addTimeFor && (
        <AddTimeModal item={addTimeFor} atReference={atReference} onClose={() => setAddTimeFor(null)}
          onLogged={({ ticketNumber, hours }) => {
            const row = addTimeFor;
            setAddTimeFor(null);
            flash(`Logged ${hours}h to ${ticketNumber}`);
            recheck(row, row.company.id);
          }} />
      )}
    </div>
  );
}
